import { useState } from "react"
import { toTodayInputDate } from "@/domain/common/utils/DateUtils"
import { useWeightClassChoices } from "@/domain/fight/hooks/WeightClassChoicesHook"
import { EntryClient } from "@/domain/entry/client/EntryClient"
import {
  MIN_ENTRY_PASSWORD_LENGTH,
  PHONE_NO_PATTERN,
} from "@/domain/entry/model/Entry"
import { isPlayerRecordCollectedByValue } from "@/domain/fight/model/Fight"
import type {
  EntryGymErrors,
  EntryGymInput,
  EntryPasswordErrors,
  EntryPasswordInput,
  EntryPlayerErrors,
  EntryPlayerInput,
  EntryPlayerTextField,
} from "@/domain/entry/model/Entry"

/** 체급 선택지가 달라지는 입력 — 이 값이 바뀌면 부 · 성별이 바뀌므로 다시 묻는다 */
const WEIGHT_CLASS_TRIGGER_FIELDS: EntryPlayerTextField[] = [
  "sport",
  "birthDate",
  "gender",
]

function makeInitGym(): EntryGymInput {
  return { name: "", contactName: "", contactPhoneNo: "" }
}

function makeInitPassword(): EntryPasswordInput {
  return { password: "", passwordConfirm: "" }
}

function makeInitPlayer(): EntryPlayerInput {
  return {
    key: crypto.randomUUID(),
    sport: "",
    name: "",
    birthDate: "",
    gender: "",
    weightKg: "",
    careerYears: "",
    competitionCount: "",
    weightClass: null,
  }
}

function validateGym(gym: EntryGymInput): EntryGymErrors {
  const errors: EntryGymErrors = {}

  if (!gym.name.trim()) errors.name = "체육관명을 입력해 주세요."
  if (!gym.contactName.trim()) errors.contactName = "담당자명을 입력해 주세요."
  if (!PHONE_NO_PATTERN.test(gym.contactPhoneNo.replaceAll(" ", ""))) {
    errors.contactPhoneNo = "휴대폰 번호 형식을 확인해 주세요."
  }

  return errors
}

/** 신청 비밀번호 — 조회 · 수정 · 취소에 쓰므로 확인 입력과 함께 본다 */
function validatePassword(input: EntryPasswordInput): EntryPasswordErrors {
  const errors: EntryPasswordErrors = {}

  if (input.password.length < MIN_ENTRY_PASSWORD_LENGTH) {
    errors.password = `비밀번호를 ${MIN_ENTRY_PASSWORD_LENGTH}자 이상 입력해 주세요.`
  }
  if (input.password !== input.passwordConfirm) {
    errors.passwordConfirm = "비밀번호가 일치하지 않습니다."
  }

  return errors
}

/** 체중 · 경력 · 출전 횟수 — 받는 종목에만 있는 칸이다 (닭싸움은 칸 자체가 없다) */
function validatePlayerRecord(player: EntryPlayerInput): EntryPlayerErrors {
  const errors: EntryPlayerErrors = {}
  const competitionCount = Number(player.competitionCount)

  if (player.weightKg === "" || Number(player.weightKg) <= 0) {
    errors.weightKg = "0보다 큰 몸무게를 입력해 주세요."
  }
  if (player.careerYears === "" || Number(player.careerYears) < 0) {
    errors.careerYears = "0 이상의 경력을 입력해 주세요."
  }
  if (
    player.competitionCount === "" ||
    !Number.isInteger(competitionCount) ||
    competitionCount < 0
  ) {
    errors.competitionCount = "0 이상의 정수를 입력해 주세요."
  }

  return errors
}

/**
 * 선수 한 명 검증
 *
 * `hasWeightClassChoice` 는 그 선수가 고를 체급이 있는지다 — 체급표가 없는 부(아마추어복싱
 * 중 · 고등부)는 고를 수가 없어 서버도 미분류로 접수하므로 필수로 보지 않는다.
 * 닭싸움은 이름 · 생년월일 · 성별 · 종목 넷만 받으므로 체급 · 기록을 보지 않는다.
 */
function validatePlayer(
  player: EntryPlayerInput,
  hasWeightClassChoice: boolean,
): EntryPlayerErrors {
  const isRecordCollected = isPlayerRecordCollectedByValue(player.sport)
  const errors: EntryPlayerErrors = isRecordCollected
    ? validatePlayerRecord(player)
    : {}
  const isFutureBirthDate =
    Boolean(player.birthDate) && player.birthDate > toTodayInputDate()

  if (isRecordCollected && hasWeightClassChoice && !player.weightClass) {
    errors.weightClass = "체급을 선택해 주세요."
  }
  if (!player.sport) errors.sport = "종목을 선택해 주세요."
  if (!player.name.trim()) errors.name = "이름을 입력해 주세요."
  if (!player.birthDate) errors.birthDate = "생년월일을 입력해 주세요."
  if (isFutureBirthDate) {
    errors.birthDate = "생년월일은 오늘 이후로 입력할 수 없습니다."
  }
  if (!player.gender) errors.gender = "성별을 선택해 주세요."

  return errors
}

/** 이름 · 생년월일 · 성별이 모두 같은 선수는 서버도 중복으로 거른다 */
function hasDuplicatePlayer(players: EntryPlayerInput[]): boolean {
  const identities = new Set<string>()

  return players.some((player) => {
    if (!player.name || !player.birthDate || !player.gender) return false

    const identity = `${player.name.trim()}|${player.birthDate}|${player.gender}`
    if (identities.has(identity)) return true

    identities.add(identity)
    return false
  })
}

/**
 * 참가 신청 폼 훅.
 * 폼 상태는 화면 밖에서 공유할 일이 없으므로 Store 없이 지역 상태로 관리한다.
 */
export function useEntryForm(tournamentId: string) {
  const [gym, setGym] = useState<EntryGymInput>(makeInitGym)
  const [passwordInput, setPasswordInput] =
    useState<EntryPasswordInput>(makeInitPassword)
  const [players, setPlayers] = useState<EntryPlayerInput[]>(() => [
    makeInitPlayer(),
  ])
  const [gymErrors, setGymErrors] = useState<EntryGymErrors>({})
  const [passwordErrors, setPasswordErrors] = useState<EntryPasswordErrors>({})
  const [playerErrors, setPlayerErrors] = useState<
    Record<string, EntryPlayerErrors>
  >({})
  const [isAgreed, setIsAgreed] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [registeredEntryNo, setRegisteredEntryNo] = useState("")
  const { getChoices, loadChoices } = useWeightClassChoices()

  function setGymField(field: keyof EntryGymInput, value: string) {
    setGym((current) => ({ ...current, [field]: value }))
  }

  function setPasswordField(field: keyof EntryPasswordInput, value: string) {
    setPasswordInput((current) => ({ ...current, [field]: value }))
  }

  function setPlayerField(
    key: string,
    field: EntryPlayerTextField,
    value: string,
  ) {
    // 종목 · 생년월일 · 성별이 바뀌면 고를 체급이 달라진다 — 고른 값을 버리고 다시 묻는다
    const isWeightClassReset = WEIGHT_CLASS_TRIGGER_FIELDS.includes(field)

    setPlayers((current) =>
      current.map((player) => {
        if (player.key !== key) return player
        if (isWeightClassReset) {
          return { ...player, [field]: value, weightClass: null }
        }

        return { ...player, [field]: value }
      }),
    )

    if (!isWeightClassReset) return

    const changedPlayer = players.find((player) => player.key === key)
    if (!changedPlayer) return

    void loadChoices({ tournamentId, ...changedPlayer, [field]: value })
  }

  function setPlayerWeightClass(key: string, optionKey: string) {
    const selected = getChoices(key).options.find(
      (option) => option.key === optionKey,
    )

    setPlayers((current) =>
      current.map((player) =>
        player.key === key
          ? { ...player, weightClass: selected ?? null }
          : player,
      ),
    )
  }

  function addPlayer() {
    setPlayers((current) => [...current, makeInitPlayer()])
  }

  function removePlayer(key: string) {
    setPlayers((current) => current.filter((player) => player.key !== key))
  }

  function setAgreed(agreed: boolean) {
    setIsAgreed(agreed)
  }

  /** 검증 결과를 상태에 반영하고 화면에 띄울 메시지를 돌려준다 */
  function validate(): string {
    const nextGymErrors = validateGym(gym)
    const nextPasswordErrors = validatePassword(passwordInput)
    const nextPlayerErrors: Record<string, EntryPlayerErrors> = {}

    players.forEach((player) => {
      const hasWeightClassChoice = getChoices(player.key).options.length > 0
      const errors = validatePlayer(player, hasWeightClassChoice)
      if (Object.keys(errors).length > 0) nextPlayerErrors[player.key] = errors
    })

    setGymErrors(nextGymErrors)
    setPasswordErrors(nextPasswordErrors)
    setPlayerErrors(nextPlayerErrors)

    if (hasDuplicatePlayer(players)) {
      return "이미 추가된 선수입니다. 이름, 생년월일, 성별이 같은 선수를 확인해 주세요."
    }
    if (
      Object.keys(nextGymErrors).length > 0 ||
      Object.keys(nextPasswordErrors).length > 0 ||
      Object.keys(nextPlayerErrors).length > 0
    ) {
      return "필수 입력 항목을 확인해 주세요."
    }
    if (!isAgreed) {
      return "허위사실 기재 관련 동의가 필요합니다."
    }

    return ""
  }

  async function submit() {
    const validationMessage = validate()

    if (validationMessage) {
      setErrorMessage(validationMessage)
      return
    }

    setErrorMessage("")
    setIsSubmitting(true)

    try {
      const entryNo = await EntryClient.register({
        tournamentId,
        gym,
        players,
        isFalseStatementAgreed: isAgreed,
        password: passwordInput.password,
      })
      setRegisteredEntryNo(entryNo)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "신청에 실패했습니다.",
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    gym,
    passwordInput,
    players,
    gymErrors,
    passwordErrors,
    playerErrors,
    isAgreed,
    errorMessage,
    isSubmitting,
    registeredEntryNo,
    setGymField,
    setPasswordField,
    setPlayerField,
    setPlayerWeightClass,
    getWeightClassChoices: getChoices,
    addPlayer,
    removePlayer,
    setAgreed,
    submit,
  }
}
